/**
 * Sélection des périphériques audio (micro d'entrée + haut-parleur de sortie).
 *
 * L'agent choisit, dans la page Paramètres, QUEL micro capter et SUR QUEL
 * haut-parleur jouer la voix du correspondant — utile quand la machine a
 * plusieurs entrées/sorties (casque, micro intégré, enceintes…).
 *
 * Choix LOCAL à la machine (≠ réglages micro centraux du tenant) : chaque poste
 * a son propre matériel → on persiste en localStorage, jamais côté serveur.
 *
 * Ce module est volontairement « sans DOM » : la logique pure (fusion de la
 * contrainte deviceId, normalisation) est testable, et le SipController
 * consomme ces helpers pour appliquer le choix (getUserMedia + setSinkId).
 */

export const MIC_DEVICE_KEY = 'pexitalk:micDeviceId'
export const SPEAKER_DEVICE_KEY = 'pexitalk:speakerDeviceId'

/** Normalise un identifiant de périphérique. '' = « périphérique système par
 *  défaut » (aucune contrainte explicite). Trim + tolérance null/undefined. */
export function normalizeDeviceId(raw: string | null | undefined): string {
  if (typeof raw !== 'string') return ''
  return raw.trim()
}

/**
 * Fusionne le micro choisi dans des contraintes audio existantes (PUR).
 * Renvoie TOUJOURS un nouvel objet (ne mute pas `base` — important car les
 * niveaux de denoise sont des constantes partagées). `''` → contraintes
 * inchangées (périphérique par défaut). `deviceId: { exact }` force le micro
 * choisi (échoue proprement si débranché → l'appelant retombe sur le défaut).
 */
export function withInputDevice(
  base: MediaTrackConstraints,
  deviceId: string,
): MediaTrackConstraints {
  const id = normalizeDeviceId(deviceId)
  return id ? { ...base, deviceId: { exact: id } } : { ...base }
}

// ── Persistance (localStorage, best-effort) ────────────────────────────────

export function loadInputDeviceId(): string {
  try { return normalizeDeviceId(localStorage.getItem(MIC_DEVICE_KEY)) } catch { return '' }
}

export function saveInputDeviceId(id: string): void {
  try {
    const v = normalizeDeviceId(id)
    if (v) localStorage.setItem(MIC_DEVICE_KEY, v)
    else localStorage.removeItem(MIC_DEVICE_KEY)
  } catch { /* localStorage indisponible */ }
}

export function loadOutputDeviceId(): string {
  try { return normalizeDeviceId(localStorage.getItem(SPEAKER_DEVICE_KEY)) } catch { return '' }
}

export function saveOutputDeviceId(id: string): void {
  try {
    const v = normalizeDeviceId(id)
    if (v) localStorage.setItem(SPEAKER_DEVICE_KEY, v)
    else localStorage.removeItem(SPEAKER_DEVICE_KEY)
  } catch { /* localStorage indisponible */ }
}
